import Link from 'next/link';
import type { ReactNode } from 'react';

type EditorialMarkdownProps = {
  content: string;
  className?: string;
};

const internalLinkPattern = /\[([^\]]+)\]\((\/[^\s)]+)\)/g;
const boldPattern = /\*\*([^*]+)\*\*/g;

export function slugifyHeading(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');
}

function renderInlineText(text: string) {
  const parts: ReactNode[] = [];
  let cursor = 0;
  
  // First handle internal links
  const matches: { index: number; length: number; node: ReactNode }[] = [];
  
  let linkMatch: RegExpExecArray | null;
  const linkRegex = new RegExp(internalLinkPattern.source, 'g');
  while ((linkMatch = linkRegex.exec(text)) !== null) {
    matches.push({
      index: linkMatch.index,
      length: linkMatch[0].length,
      node: (
        <Link
          key={`link-${linkMatch.index}-${linkMatch[2]}`}
          href={linkMatch[2]}
          className="font-medium text-primary underline decoration-primary/40 underline-offset-4 hover:decoration-primary transition-colors"
        >
          {linkMatch[1]}
        </Link>
      ),
    });
  }

  // Handle bold text **bold**
  let boldMatch: RegExpExecArray | null;
  const boldRegex = new RegExp(boldPattern.source, 'g');
  while ((boldMatch = boldRegex.exec(text)) !== null) {
    // Only add if it doesn't overlap with a link
    const overlap = matches.some(
      (m) => (boldMatch!.index >= m.index && boldMatch!.index < m.index + m.length)
    );
    if (!overlap) {
      matches.push({
        index: boldMatch.index,
        length: boldMatch[0].length,
        node: <strong key={`bold-${boldMatch.index}`} className="font-semibold text-accent">{boldMatch[1]}</strong>,
      });
    }
  }

  // Sort matches by index
  matches.sort((a, b) => a.index - b.index);

  matches.forEach((m) => {
    if (m.index > cursor) {
      parts.push(text.slice(cursor, m.index));
    }
    parts.push(m.node);
    cursor = m.index + m.length;
  });

  if (cursor < text.length) {
    parts.push(text.slice(cursor));
  }

  return parts;
}

/** Enhanced Markdown Renderer with Heading Anchors, Callouts, and Rich Inline Formatting */
export default function EditorialMarkdown({ content, className = '' }: EditorialMarkdownProps) {
  const blocks: React.ReactNode[] = [];
  const lines = content.replace(/\r\n/g, '\n').split('\n');
  let paragraph: string[] = [];
  let listItems: string[] = [];
  let blockquoteItems: string[] = [];
  let tableRows: string[][] = [];

  const flushParagraph = () => {
    if (paragraph.length) {
      blocks.push(
        <p key={`p-${blocks.length}`} className="text-base sm:text-lg leading-relaxed text-text-secondary">
          {renderInlineText(paragraph.join(' '))}
        </p>
      );
      paragraph = [];
    }
  };

  const flushList = () => {
    if (listItems.length) {
      blocks.push(
        <ul key={`ul-${blocks.length}`} className="list-disc space-y-2.5 pl-6 text-base sm:text-lg leading-relaxed text-text-secondary">
          {listItems.map((item, index) => (
            <li key={index}>{renderInlineText(item)}</li>
          ))}
        </ul>
      );
      listItems = [];
    }
  };

  const flushBlockquote = () => {
    if (blockquoteItems.length) {
      blocks.push(
        <blockquote key={`bq-${blocks.length}`} className="my-6 rounded-2xl border-l-4 border-primary bg-gradient-to-r from-orange-50 via-amber-50/50 to-transparent p-5 sm:p-6 text-accent font-medium shadow-xs">
          <p className="text-base sm:text-lg leading-relaxed italic">{renderInlineText(blockquoteItems.join(' '))}</p>
        </blockquote>
      );
      blockquoteItems = [];
    }
  };

  const flushTable = () => {
    if (tableRows.length >= 2) {
      const headerRow = tableRows[0];
      const bodyRows = tableRows.slice(1);
      blocks.push(
        <div key={`table-${blocks.length}`} className="my-6 overflow-x-auto rounded-2xl border border-orange-100/80 bg-white p-1 shadow-xs">
          <table className="w-full text-left text-sm sm:text-base text-text-secondary border-collapse">
            <thead>
              <tr className="border-b border-orange-100 bg-orange-50/70">
                {headerRow.map((cell, idx) => (
                  <th key={idx} className="p-3.5 font-bold text-accent first:rounded-tl-xl last:rounded-tr-xl">
                    {renderInlineText(cell)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {bodyRows.map((row, rIdx) => (
                <tr key={rIdx} className="hover:bg-orange-50/20 transition-colors">
                  {row.map((cell, cIdx) => (
                    <td key={cIdx} className="p-3.5 align-top">
                      {renderInlineText(cell)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }
    tableRows = [];
  };

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) {
      flushParagraph();
      flushList();
      flushBlockquote();
      flushTable();
      continue;
    }

    if (trimmed.startsWith('|') && trimmed.endsWith('|')) {
      flushParagraph();
      flushList();
      flushBlockquote();
      if (trimmed.replace(/[\s|:-]/g, '') !== '') {
        const cells = trimmed.slice(1, -1).split('|').map((s) => s.trim());
        tableRows.push(cells);
      }
      continue;
    }

    flushTable();

    if (trimmed.startsWith('> ')) {
      flushParagraph();
      flushList();
      blockquoteItems.push(trimmed.slice(2));
    } else if (trimmed.startsWith('### ')) {
      flushParagraph();
      flushList();
      flushBlockquote();
      const headingText = trimmed.slice(4);
      const headingId = slugifyHeading(headingText);
      blocks.push(
        <h3
          id={headingId}
          key={`h3-${blocks.length}`}
          className="pt-4 text-xl sm:text-2xl font-serif font-bold text-accent scroll-mt-24"
        >
          {renderInlineText(headingText)}
        </h3>
      );
    } else if (trimmed.startsWith('## ')) {
      flushParagraph();
      flushList();
      flushBlockquote();
      const headingText = trimmed.slice(3);
      const headingId = slugifyHeading(headingText);
      blocks.push(
        <h2
          id={headingId}
          key={`h2-${blocks.length}`}
          className="pt-6 pb-2 text-2xl sm:text-3xl font-serif font-bold leading-tight text-accent border-b border-orange-100/60 scroll-mt-24"
        >
          {renderInlineText(headingText)}
        </h2>
      );
    } else if (trimmed.startsWith('- ')) {
      flushParagraph();
      flushBlockquote();
      listItems.push(trimmed.slice(2));
    } else {
      const imgMatch = /!\[([^\]]*)\]\(([^)]+)\)/.exec(trimmed);
      if (imgMatch) {
        flushParagraph();
        flushList();
        flushBlockquote();
        const alt = imgMatch[1];
        const src = imgMatch[2];
        blocks.push(
          <figure key={`img-${blocks.length}`} className="my-8 overflow-hidden rounded-2xl border border-orange-100/80 bg-orange-50/20 shadow-sm">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src={src} alt={alt} className="h-auto w-full max-h-[500px] object-cover" />
            {alt && <figcaption className="p-3 text-center text-xs font-medium text-text-secondary bg-orange-50/40 border-t border-orange-100/50">{alt}</figcaption>}
          </figure>
        );
      } else {
        flushList();
        flushBlockquote();
        paragraph.push(trimmed);
      }
    }
  }

  flushParagraph();
  flushList();
  flushBlockquote();
  flushTable();

  return <div className={`space-y-6 ${className}`}>{blocks}</div>;
}
